import { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';

import { PREFERENCE_FEEDBACK_MESSAGES } from '@shared/guidedPlanningPreferences';

import { useSession, useIsSessionComplete } from '@/features/guided-financial-planning/context';
import { usePreferenceStepHandlers } from '@/features/guided-financial-planning/hooks';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';
import { PreferenceFeedback } from '@/features/guided-financial-planning/components/preference-feedback';
import {
  RadioOptionCards,
  type RadioOption,
} from '@/features/guided-financial-planning/components/radio-option-cards';

const DEATH_BENEFITS_OPTIONS: RadioOption<DeathBenefitsPreference>[] = [
  {
    value: 'yes',
    title: 'Yes, That Matters to Me',
    description: 'I want to leave something behind',
  },
  {
    value: 'no',
    title: 'No, Not a Priority',
    description: 'Focus on my care needs instead',
  },
];

export function PreferencesDeathBenefitsStep() {
  const { session } = useSession();
  const isSessionComplete = useIsSessionComplete();

  const sessionValue = session?.stepData?.preferences?.deathBenefits;
  const [deathBenefits, setDeathBenefits] = useState<DeathBenefitsPreference | undefined>(
    sessionValue,
  );

  useEffect(() => {
    if (sessionValue && !deathBenefits) {
      setDeathBenefits(sessionValue);
    }
  }, [sessionValue, deathBenefits]);

  const { handleNext, handleBack, isSaving } = usePreferenceStepHandlers({
    preferenceKey: 'deathBenefits',
    value: deathBenefits,
  });

  const isValid = deathBenefits !== undefined;

  const feedbackMessage = deathBenefits
    ? PREFERENCE_FEEDBACK_MESSAGES.deathBenefits[deathBenefits]
    : null;

  return (
    <StepLayout
      title="Do you care about death benefits?"
      subtitle="Would leaving a legacy to your children or loved ones be important to you?"
      icon={
        <div className="flex flex-col items-center gap-3">
          <span className="text-sm font-medium text-gray-500">Question 1 of 3</span>
          <div className="bg-primary/10 flex size-14 items-center justify-center rounded-full">
            <Heart className="text-primary size-7" />
          </div>
        </div>
      }
      nextButtonLabel="Continue"
      isNextDisabled={!isValid || isSaving}
      onNext={handleNext}
      onBack={handleBack}
      isLoading={isSaving}
    >
      <div className="flex flex-col items-center">
        <RadioOptionCards
          options={DEATH_BENEFITS_OPTIONS}
          value={deathBenefits}
          onChange={setDeathBenefits}
          className="w-full max-w-lg"
          disabled={isSessionComplete}
        />
        <div className="w-full max-w-lg">
          <PreferenceFeedback message={feedbackMessage} />
        </div>
        <div className="mt-8 flex justify-center gap-2">
          <div className="bg-primary size-2 rounded-full" />
          <div className="size-2 rounded-full bg-gray-300" />
          <div className="size-2 rounded-full bg-gray-300" />
        </div>
      </div>
    </StepLayout>
  );
}
